import { useReduxState } from 'hooks';
import api from 'api';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { Alert, ConfirmButton, ModalButton } from 'components/common';
import { TwoFactorBackupCodes } from 'components/common/auth';

interface Props {
  affectedBySharedTwoFactor?: boolean;
  isSharedConfiguration?: boolean;
  numberOfBackupCodes: number;
  onUpdate: () => Promise<void> | void;
}

const ProfileAuthTwoFactorActive = ({
  affectedBySharedTwoFactor,
  isSharedConfiguration,
  numberOfBackupCodes,
  onUpdate
}: Props) => {
  const {
    messages,
    serverConfig: { authRequireTwoFactor, authRequireTwoFactorConditions },
    theme,
    user
  } = useReduxState();

  const disable = async () => {
    await api.post(
      '/auth/totp/disable' +
        (isSharedConfiguration ? '?isSharedConfigured=true' : '')
    );
    await onUpdate();
    return true;
  };

  const generateNewBackupCodes = async () => {
    const response = await api.post(
      `/auth/totp/backup-codes` +
        (isSharedConfiguration ? '?isSharedConfigured=true' : '')
    );
    if (response.data?.success && response.data?.backupCodes) {
      await onUpdate();
      return true;
    }
    return false;
  };

  const cannotDisable =
    !isSharedConfiguration &&
    ((authRequireTwoFactor === 'admins' && user?.roles?.includes('admin')) ||
      (authRequireTwoFactor === 'all' && user?.roles?.includes('user'))) &&
    attributesConditionsMatch({
      conditions: authRequireTwoFactorConditions?.conditions,
      customAttributeValues: user?.customAttributes,
      user
    });

  return (
    <div className="active">
      <div className="active-indicator">
        <Alert
          {...(affectedBySharedTwoFactor
            ? {
                text: `${messages.authTwoFactorAuthenticationSharedInUse} ${messages.authTwoFactorAuthenticationSharedConflict}`,
                type: 'warning-light'
              }
            : {
                text: messages.authTwoFactorAuthenticationActive,
                type: 'success-light'
              })}
        />
      </div>

      <div className="section">
        <div className="title">{messages.authTwoFactorBackupCodes}</div>

        <div className="content">
          {messages.authTwoFactorBackupCodesUnused}
          {` ${numberOfBackupCodes} `}
          {messages.authTwoFactorBackupCodesUnusedAfter}
        </div>

        <div className="content">
          {numberOfBackupCodes > 0 ? (
            <ModalButton
              category="light"
              style={{ marginRight: 10 }}
              modalProps={{
                closeButton: true,
                title: messages.authTwoFactorBackupCodes
              }}
              text={messages.authTwoFactorShowCodes}
            >
              <TwoFactorBackupCodes
                isSharedConfigured={isSharedConfiguration}
              />
            </ModalButton>
          ) : null}

          <ConfirmButton
            actionText={messages.authTwoFactorGenerateNewCodes}
            buttonText={messages.authTwoFactorGenerateNewCodes}
            confirmText={messages.authTwoFactorGenerateNewCodesInfo}
            confirmTitle={messages.authTwoFactorGenerateNewCodes}
            onConfirm={generateNewBackupCodes}
            successMessage={messages.authTwoFactorGenerateNewCodesSuccess}
          />
        </div>
      </div>

      <div className="section">
        <div className="title">{messages.authTwoFactorDisable}</div>

        {cannotDisable ? (
          <div className="content">
            {`${messages.authTwoFactorRequired} ${messages.authTwoFactorRequiredNoDisable}`}
          </div>
        ) : null}

        <div className="content">
          <ConfirmButton
            actionText={messages.commonDisable}
            buttonText={messages.commonDisable}
            category="danger"
            confirmText={messages.authTwoFactorDisableInfo}
            confirmTitle={messages.authTwoFactorDisable}
            disabled={cannotDisable}
            onConfirm={disable}
            successMessage={messages.authTwoFactorDisableSuccess}
          />
        </div>
      </div>

      <style jsx>{`
        .section {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          margin-top: 15px;
          padding: 15px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 600;
        }
        .content {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default ProfileAuthTwoFactorActive;
